import React, { useEffect, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { Row, Col, Modal, Button } from "react-bootstrap";
import Card from "../../../components/Card";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { EtsyAPI } from "../../../js/api";
import { handleApiError } from "../../../js/helper/errorHandler";
import EtsyConnectModal from "../components/EtsyConnectModal";
import "./styles.css";

const ListingList = () => {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEtsyModal, setShowEtsyModal] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // ================= FETCH ACCOUNTS =================
  const fetchAccounts = async () => {
    try {
      setIsLoading(true);
      const res = await EtsyAPI.getAccounts();

      if (res.data.status === 200) {
        setAccounts(res.data.data.accounts);
      } else {
        handleApiError(res.data, "Failed to fetch accounts");
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch accounts");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // ================= SYNC LISTINGS =================
  const handleSyncListings = async (account) => {
    Swal.fire({
      title: "Sync Listings?",
      text: `This will fetch all listings from ${account.shop_name}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sync",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        setIsLoading(true);

        const res = await EtsyAPI.syncListings(account._id);

        if (res.data.status === 200) {
          toast.success(
            "Sync started in background. Listings will be updated shortly.",
          );
        } else {
          handleApiError(res.data, "Sync failed");
        }
      } catch (err) {
        handleApiError(err, "Sync failed");
      } finally {
        setIsLoading(false);
      }
    });
  };

  return (
    <>
      <EtsyConnectModal
        show={showEtsyModal}
        onHide={() => setShowEtsyModal(false)}
      />

      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <h4 className="card-title">Etsy Accounts</h4>
            </Card.Header>

            <Card.Body style={{ position: "relative" }}>
              {isLoading && (
                <div className="fullscreen-loader">
                  <div className="loader-content text-center">
                    <Spinner animation="border" variant="light" />

                    <h5 className="mt-3 text-white">Processing Request</h5>

                    <p className="mt-2 text-white">
                      This operation may take <strong>5–10 minutes</strong>.
                    </p>

                    <p className="text-warning mb-0">
                      ⚠️ Please do not refresh or close this page.
                    </p>
                  </div>
                </div>
              )}

              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Shop</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {accounts.map((account, index) => (
                      <tr key={account._id}>
                        <td>{index + 1}</td>
                        <td>{account.shop_name}</td>

                        <td>
                          <span
                            className={`badge ${
                              account.is_active ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {account.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td>
                          <div className="d-flex gap-2">
                            {/* SYNC */}
                            <Button
                              size="sm"
                              variant="warning"
                              onClick={() => handleSyncListings(account)}
                            >
                              Sync
                            </Button>

                            {/* DETAILS */}
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setSelectedAccount(account);
                                setShowModal(true);
                              }}
                            >
                              Details
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* DETAILS MODAL */}
      {selectedAccount && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Account Details</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p>
              <b>Shop:</b> {selectedAccount.shop_name}
            </p>
            <p>
              <b>Status:</b> {selectedAccount.is_active ? "Active" : "Inactive"}
            </p>
            <p>
              <b>Token Expiry:</b>{" "}
              {new Date(selectedAccount.token_expires_at).toLocaleString()}
            </p>
          </Modal.Body>
        </Modal>
      )}

      <footer className="text-center mt-5">
        <p style={{ fontSize: 14 }}>
          This application uses Etsy API but is not endorsed or certified by
          Etsy.
        </p>
      </footer>
    </>
  );
};

export default ListingList;
